import Keycloak from "keycloak-js";
import type { ControlState, Domain, Jurisdiction, Stage, SystemFlags, SystemRole, TenantRole, Tier, Coverage, UsState } from "@aigov/domain";

declare global {
  interface Window { AIGOV_CONFIG?: { keycloakUrl: string; realm: string; clientId: string } }
}

const cfg = window.AIGOV_CONFIG ?? { keycloakUrl: `${location.origin}/auth`, realm: "aigov", clientId: "aigov-web" };
export const keycloak = new Keycloak({ url: cfg.keycloakUrl, realm: cfg.realm, clientId: cfg.clientId });

export async function initAuth(): Promise<void> {
  await keycloak.init({ onLoad: "login-required", pkceMethod: "S256", checkLoginIframe: false });
}

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function authHeader(): Promise<Record<string, string>> {
  try { await keycloak.updateToken(30); } catch { await keycloak.login(); }
  return { Authorization: `Bearer ${keycloak.token}` };
}

export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = await authHeader();
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(`/api${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (data as { message?: string }).message ?? `Request failed (${res.status})`);
  return data as T;
}

export async function download(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(`/api${path}`, { headers: await authHeader() });
  if (!res.ok) throw new ApiError(res.status, "Export failed");
  const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.append(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface TenantSummary { id: string; name: string; slug: string; region: Jurisdiction; role: TenantRole }
export interface Me {
  id: string; email: string; name: string; platformAdmin: boolean; tenants: TenantSummary[];
  disclaimer: { version: string; acknowledgedAt: string | null };
}

export interface SystemDto {
  id: string; name: string; ownerName: string; description: string; role: SystemRole;
  jurisdictions: Jurisdiction[]; usStates: UsState[]; domain: Domain; stage: Stage; flags: SystemFlags;
  libraryVersion: string; version: number; createdAt: string; updatedAt: string;
  controls: Record<string, ControlState & { updatedAt: string }>;
  summary: { tier: Tier; coverage: Coverage; nextDeadline: string | null; stale: boolean };
}
export type SystemInput = Pick<SystemDto, "name" | "ownerName" | "description" | "role" | "jurisdictions" | "usStates" | "domain" | "stage" | "flags">;

export interface Member { id: string; email: string; name: string; role: TenantRole; status: "active" | "invited"; lastSeen: string | null }

export interface AuditEntry {
  id: number; at: string; actor: string; requestId: string; action: string; entity: string; entityId: string;
  before: Record<string, unknown> | null; after: Record<string, unknown> | null; hash: string;
}

export interface LibraryDto {
  version: string; baseline: string;
  verifications: { controlId: string; framework: string; verifiedAt: string; note: string }[];
}
